import React, {
  createContext,
  useCallback,
  useContext,
  PropsWithChildren,
} from 'react';

export type ImageRegistry = Record<string, string>;

interface ImageContextValue {
  images: ImageRegistry; // CDN URLs (optimistic); fall back per-image via onError
  localImages: ImageRegistry; // always local — used as the onError fallback
}

const ImageContext = createContext<ImageContextValue>({
  images: {},
  localImages: {},
});

interface ImageProviderProps {
  images: ImageRegistry;
  localImages: ImageRegistry;
}

export const ImageProvider: React.FC<PropsWithChildren<ImageProviderProps>> = ({
  images,
  localImages,
  children,
}) => (
  <ImageContext.Provider value={{ images, localImages }}>
    {children}
  </ImageContext.Provider>
);

/** Returns the active image registry (CDN or local, resolved at startup). */
export const useImages = () => useContext(ImageContext).images;

/**
 * Always returns local bundled URLs — use this when you need the actual file
 * content rather than a CDN URL (e.g. inline SVG rendering via dangerouslySetInnerHTML).
 */
export const useLocalImages = () => useContext(ImageContext).localImages;

/** Like useImage but callable per name. imgProps for <img>, img for CSS url(). */
export const useImageUrl = () => {
  const { images, localImages } = useContext(ImageContext);

  const img = useCallback(
    (key: string): string => images[key] ?? localImages[key],
    [images, localImages]
  );

  const imgProps = useCallback(
    (key: string) => ({
      src: images[key] ?? localImages[key],
      onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
        const fallback = localImages[key];
        if (fallback && e.currentTarget.src !== fallback) {
          e.currentTarget.src = fallback;
        }
      },
    }),
    [images, localImages]
  );

  return { img, imgProps };
};

/** Returns { src, onError } — onError swaps to local if the CDN src fails. */
export const useImage = (key: string) => {
  const { images, localImages } = useContext(ImageContext);
  const primary = images[key];
  const fallback = localImages[key];

  const onError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (fallback && e.currentTarget.src !== fallback) {
      e.currentTarget.src = fallback;
    }
  };

  return { src: primary ?? fallback, onError };
};

export default ImageContext;
